import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CorrectionChange,
  DaySummary,
  DiaryEntry,
  ProfileInput,
  Product,
  SavedMeal,
  UnresolvedItem,
  User,
  QuantityUnit,
} from '@calorie/shared';
import { entryTimestamp, mealByHour } from '@calorie/shared';
import { api } from './lib/api.js';
import { shiftISO, todayISO } from './lib/dates.js';
import { hapticResult, isTelegram, tzOffsetMinutes } from './lib/telegram.js';
import { SEARCH_STAGE_AFTER_MS, type PendingEntry } from './lib/pending.js';
import { Composer } from './components/Composer.js';
import { BarcodeSheet } from './components/BarcodeSheet.js';
import { EntryDetails } from './components/EntryDetails.js';
import { MealsSheet } from './components/MealsSheet.js';
import { Splash } from './components/Splash.js';
import { Onboarding } from './pages/Onboarding.js';
import { Settings } from './pages/Settings.js';
import { Today } from './pages/Today.js';
import { WeightDetails } from './pages/WeightDetails.js';

type Status = 'loading' | 'onboarding' | 'ready' | 'error';

export function App() {
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [weightDetailsOpen, setWeightDetailsOpen] = useState(false);
  const [today, setToday] = useState(todayISO);
  const [date, setDate] = useState(today);
  const activeDate = useRef(date);
  activeDate.current = date;
  const dayRequest = useRef(0);
  const [day, setDay] = useState<DaySummary | null>(null);
  const [profile, setProfile] = useState<ProfileInput | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [pending, setPending] = useState<PendingEntry[]>([]);
  const [unresolved, setUnresolved] = useState<UnresolvedItem[]>([]);
  const [unresolvedDate, setUnresolvedDate] = useState(date);
  const [meals, setMeals] = useState<SavedMeal[]>([]);
  const [mealsOpen, setMealsOpen] = useState(false);
  const [detailsEntry, setDetailsEntry] = useState<DiaryEntry | null>(null);
  const [barcodeOpen, setBarcodeOpen] = useState(false);

  const refreshDay = useCallback(async (targetDate = activeDate.current) => {
    if (targetDate !== activeDate.current) return;
    const request = ++dayRequest.current;
    const summary = await api.getDay(targetDate);
    if (activeDate.current === targetDate && dayRequest.current === request) setDay(summary);
  }, []);

  // Вход по initData: один раз при старте
  useEffect(() => {
    (async () => {
      try {
        const auth = await api.login();
        setUser(auth.user);
        if (!auth.goals) {
          setStatus('onboarding');
          return;
        }
        // Анкета нужна, чтобы настройки открывались с реальными значениями,
        // а не с дефолтами, которые молча затёрли бы профиль при сохранении.
        setProfile((await api.getGoals())?.profile ?? null);
        setStatus('ready');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Не удалось подключиться');
        setStatus('error');
      }
    })();
  }, []);

  // Сводка перезагружается при каждой смене дня
  useEffect(() => {
    if (status !== 'ready') return;
    let stale = false;
    (async () => {
      try {
        await refreshDay(date);
      } catch (err) {
        if (!stale) {
          setError(err instanceof Error ? err.message : 'Не удалось загрузить день');
          setStatus('error');
        }
      }
    })();
    return () => {
      // Быстрое перелистывание не должно оставлять на экране устаревший ответ
      stale = true;
    };
  }, [status, date, refreshDay]);

  // Приложение живёт открытым сутками - дата «сегодня» не должна застревать
  useEffect(() => {
    const id = setInterval(() => setToday(todayISO()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Сохранённые блюда - один раз при готовности. Их отсутствие не критично:
  // просто список будет пуст, дневник от этого не ломается.
  useEffect(() => {
    if (status !== 'ready') return;
    api.listMeals().then(setMeals).catch(() => {});
  }, [status]);

  /** Сохраняет анкету, не уводя с экрана: настройки пишутся автоматически. */
  const persistGoals = useCallback(async (input: ProfileInput) => {
    const goals = await api.saveGoals(input);
    setProfile(goals.profile);
    // Норма изменилась - сводка дня должна её подхватить
    await refreshDay();
  }, [refreshDay]);

  const persistName = useCallback(async (firstName: string) => {
    const next = await api.updateProfile(firstName);
    setUser(next);
    return next;
  }, []);

  async function completeOnboarding(input: ProfileInput) {
    await persistGoals(input);
    setStatus('ready');
  }

  /**
   * Отправляет написанное и сразу показывает это в ленте.
   *
   * Ждать ответа сервера, ничего не показывая, неправильно: разбор занимает
   * несколько секунд, и человек не понимает, приняли его запись или нет.
   * Поэтому строка появляется мгновенно со статусом, а калории подставляются,
   * когда сервер ответит.
   */
  async function sendText(text: string) {
    const targetDate = date;
    const id = crypto.randomUUID();
    setPending((list) => [...list, { id, text, date: targetDate, stage: 'parsing' }]);

    const toSearchStage = setTimeout(() => {
      setPending((list) =>
        list.map((p) => (p.id === id && p.stage === 'parsing' ? { ...p, stage: 'searching' } : p)),
      );
    }, SEARCH_STAGE_AFTER_MS);

    try {
      const result = await api.addByText(text, targetDate);
      setUnresolvedDate(targetDate);
      setUnresolved(result.unresolved);
      hapticResult(result.entry ? 'success' : 'warning');

      // Показываем ответ сразу, не дожидаясь перезагрузки дня
      if (result.entry) {
        const entry = result.entry;
        setDay((d) => (d?.date === targetDate ? { ...d, entries: [...d.entries, entry] } : d));
      }
      setPending((list) => list.filter((p) => p.id !== id));
      await refreshDay(targetDate);
    } catch {
      hapticResult('error');
      // Строку не убираем: человек видит, что именно не отправилось
      setPending((list) =>
        list.map((p) => (p.id === id ? { ...p, stage: 'failed' } : p)),
      );
    } finally {
      clearTimeout(toSearchStage);
    }
  }

  /** Кладёт готовое блюдо в дневник: сразу показываем, сервер лишь подтверждает. */
  async function addMeal(meal: SavedMeal) {
    const targetDate = date;

    const optimistic = {
      id: `tmp-${crypto.randomUUID()}`,
      text: meal.title,
      mealType: mealByHour(new Date().getHours()),
      eatenAt: entryTimestamp(targetDate, tzOffsetMinutes(targetDate)),
      kcal: meal.kcal,
      protein: meal.protein,
      fat: meal.fat,
      carbs: meal.carbs,
      items: meal.items,
    };
    setDay((d) => (d?.date === targetDate ? { ...d, entries: [...d.entries, optimistic] } : d));

    try {
      await api.addMeal(meal.id, targetDate);
      hapticResult('success');
      await refreshDay(targetDate);
    } catch {
      hapticResult('error');
      // Убираем оптимистичную строку - запись не прошла
      setDay((d) =>
        d ? { ...d, entries: d.entries.filter((e) => e.id !== optimistic.id) } : d,
      );
    }
  }

  /** Записывает упаковку, уже найденную по штрихкоду, с указанным весом. */
  async function addScannedProduct(product: Product, amount: number, unit: QuantityUnit) {
    const targetDate = date;
    const entry = await api.addProductEntry(product.id, amount, unit, targetDate);
    setDay((d) => (d?.date === targetDate ? { ...d, entries: [...d.entries, entry] } : d));
    hapticResult('success');
    await refreshDay(targetDate);
  }

  /** Разбирает состав через ИИ и запоминает блюдо. */
  async function createMeal(title: string, text: string) {
    const res = await api.createMeal(title, text);
    if (res.meal) setMeals((list) => [res.meal!, ...list]);
    return res;
  }

  async function deleteMeal(id: string) {
    const previous = meals;
    setMeals((list) => list.filter((m) => m.id !== id));
    try {
      await api.deleteMeal(id);
    } catch {
      hapticResult('error');
      setMeals(previous);
    }
  }

  /** Применяет подтверждённую правку записи и обновляет её на экране. */
  async function applyCorrection(entryId: string, changes: CorrectionChange[]) {
    const updated = await api.correctionApply(entryId, changes);
    setDay((d) =>
      d ? { ...d, entries: d.entries.map((e) => (e.id === entryId ? updated : e)) } : d,
    );
    await refreshDay();
  }

  async function deleteEntry(entryId: string) {
    const previous = day;
    // Оптимистично убираем строку: сеть в Telegram бывает медленной
    setDay((d) =>
      d ? { ...d, entries: d.entries.filter((e) => e.id !== entryId) } : d,
    );
    try {
      await api.deleteEntry(entryId);
      await refreshDay();
    } catch {
      hapticResult('error');
      setDay(previous);
    }
  }

  if (!isTelegram) {
    return (
      <div className="app">
        <div className="card">
          <h1 className="title">Откройте приложение из Telegram</h1>
          <p className="hint">
            Мини-апп получает данные о пользователе от клиента Telegram, поэтому
            в обычном браузере он работать не может. Напишите боту команду
            /start и нажмите кнопку «Открыть трекер».
          </p>
        </div>
      </div>
    );
  }

  if (status === 'loading') return <Splash />;

  if (status === 'error') {
    return (
      <div className="app">
        <div className="card">
          <h1 className="title">Что-то пошло не так</h1>
          <p className="error">{error}</p>
          <button className="btn" onClick={() => location.reload()}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (status === 'onboarding') {
    return <Onboarding onSave={completeOnboarding} />;
  }

  if (weightDetailsOpen) return <WeightDetails onBack={() => setWeightDetailsOpen(false)} />;

  if (settingsOpen && user) {
    return (
      <Settings
        user={user}
        initial={profile}
        onSave={persistGoals}
        onUpdateName={persistName}
        onBack={() => setSettingsOpen(false)}
        onWeightDetails={() => setWeightDetailsOpen(true)}
      />
    );
  }

  return (
    <>
      {day?.date === date ? (
        <Today
          day={day}
          today={today}
          pending={pending.filter((p) => p.date === date)}
          onDismissPending={(id) =>
            setPending((list) => list.filter((p) => p.id !== id))
          }
          unresolved={unresolvedDate === date ? unresolved : []}
          onDismissUnresolved={() => setUnresolved([])}
          onDelete={deleteEntry}
          onOpenDetails={setDetailsEntry}
          onSettings={() => setSettingsOpen(true)}
          onShiftDay={(days) => setDate((d) => shiftISO(d, days))}
          onToday={() => setDate(today)}
        />
      ) : (
        <div className="spinner" />
      )}

      {/* Поле остаётся активным: следующую запись можно писать, пока считается
          предыдущая - они не мешают друг другу */}
      <Composer
        onSend={sendText}
        onOpenMeals={() => setMealsOpen(true)}
        onOpenScanner={() => setBarcodeOpen(true)}
        busy={false}
      />

      {barcodeOpen && (
        <BarcodeSheet
          onLookup={api.getProductByBarcode}
          onCreate={api.createProduct}
          onAdd={addScannedProduct}
          onClose={() => setBarcodeOpen(false)}
        />
      )}

      {mealsOpen && (
        <MealsSheet
          meals={meals}
          onAdd={addMeal}
          onDelete={deleteMeal}
          onCreate={createMeal}
          onClose={() => setMealsOpen(false)}
        />
      )}

      {detailsEntry && (
        <EntryDetails
          entry={detailsEntry}
          onPreview={(text) => api.correctionPreview(detailsEntry.id, text)}
          onApply={(changes) => applyCorrection(detailsEntry.id, changes)}
          onClose={() => setDetailsEntry(null)}
        />
      )}
    </>
  );
}
